import React from "react";
import { useOutletContext } from "react-router-dom";
import { IMG, ADDRESS } from "../lib/images";
import { Reveal } from "../components/site/Reveal";
import { RibbonMarquee } from "../components/site/Marquee";
import { useSeo } from "../components/site/useSeo";

const VALUES = [
  { t: "Scratch Cooking", c: "Everything begins with real ingredients and recipes made by hand, the old-fashioned way." },
  { t: "Southern Hospitality", c: "We treat every guest like family, because that's how good food is meant to be shared." },
  { t: "Louisiana Influence", c: "A little Cajun soul runs through our kitchen, from deep roux to bold, layered flavor." },
  { t: "East Texas Roots", c: "Proudly planted in Ben Wheeler, cooking for the community we call home." },
];

export default function About() {
  const { settings } = useOutletContext();
  useSeo({
    title: "About | Hackberry Home — A Southern Kitchen in East Texas",
    description: "Hackberry Home is a Southern scratch kitchen in Ben Wheeler, TX with Louisiana influences. Scratch cooking, homemade recipes, and genuine hospitality. Come hungry. Leave like family.",
    path: "/about",
  });

  return (
    <div className="bg-hb-black">
      {/* hero */}
      <section className="relative h-[70vh] min-h-[480px] overflow-hidden grain" data-testid="about-hero">
        <img src={IMG.interior} alt="Warm rustic farmhouse restaurant interior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-hb-black/70" />
        <div className="relative z-10 h-full max-w-6xl mx-auto px-5 md:px-10 flex flex-col justify-end pb-16">
          <Reveal><p className="eyebrow text-hb-brass mb-5">Our Story</p></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-serif text-4xl md:text-7xl text-hb-cream leading-[1.02] max-w-4xl">A Southern Kitchen in the Heart of East Texas</h1>
          </Reveal>
        </div>
      </section>

      {/* intro */}
      <section className="py-24 md:py-32 relative grain">
        <div className="max-w-4xl mx-auto px-5 md:px-10 relative z-10">
          <Reveal>
            <p className="font-serif text-3xl md:text-5xl text-hb-cream leading-[1.15] mb-10">
              Some of the best meals aren't complicated — they're simply made with care.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-8 text-hb-muted leading-relaxed">
            <Reveal delay={0.05}><p>Hackberry Home was created around a simple belief: that gathering around good food is one of life's great pleasures. We take inspiration from Southern kitchens, Louisiana cooking, and the family recipes handed down through generations.</p></Reveal>
            <Reveal delay={0.1}><p>From cast-iron skillets to Sunday suppers, every dish is made from scratch with real ingredients and generous portions. This is food meant to be remembered long after the plates have been cleared — served with the kind of hospitality that makes strangers feel like neighbors.</p></Reveal>
          </div>
        </div>
      </section>

      <RibbonMarquee dark />

      {/* values */}
      <section className="bg-hb-surface py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 order-2 md:order-1">
              {VALUES.map((v, i) => (
                <Reveal key={v.t} delay={i * 0.06}>
                  <div className="border border-hb-line p-7 h-full bg-hb-black/30">
                    <p className="font-serif text-4xl text-hb-brass/50 mb-3">{String(i + 1).padStart(2, "0")}</p>
                    <h3 className="font-serif text-2xl text-hb-cream mb-2">{v.t}</h3>
                    <p className="text-hb-muted text-sm leading-relaxed">{v.c}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="order-1 md:order-2">
              <Reveal><p className="eyebrow text-hb-brass mb-5">What We Believe</p></Reveal>
              <Reveal delay={0.05}><h2 className="font-serif text-4xl md:text-6xl text-hb-cream leading-none mb-8">Built Around<br />the Table</h2></Reveal>
              <Reveal delay={0.1}><p className="text-hb-muted leading-relaxed max-w-md">Family, community, and the flavors of the South — everything we do comes back to bringing people together over a plate of something made with heart.</p></Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* gallery placeholders */}
      <section className="bg-hb-black relative grain py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10 relative z-10">
          <Reveal><p className="eyebrow text-hb-brass mb-8 text-center">Around Hackberry Home</p></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[IMG.kitchen, IMG.interior, IMG.cfs, IMG.biscuits, IMG.burger, IMG.dessert].map((img, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="border border-hb-line overflow-hidden group">
                  <img src={img} alt="Hackberry Home" className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${i === 1 || i === 4 ? "aspect-[3/4]" : "aspect-square"}`} />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* statement */}
      <section className="bg-hb-brass py-24 md:py-32" data-testid="about-statement">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <h2 className="font-serif text-5xl md:text-8xl text-hb-black leading-none">Come hungry.<br /><span className="italic">Leave like family.</span></h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-hb-black/70 eyebrow">{ADDRESS.name} · {ADDRESS.cityStateZip}</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
